import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { ArrowLeft, Clock, Edit, Plus, Printer, Save, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const TimeUnitForm = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [view, setView] = useState('list');
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    exercise_name: currentExercise?.exercise_name || '',
    incident_name: '',
    operational_period_number: '',
    operational_period_start: '',
    operational_period_end: '',
    iap_prepared_by: '',
    iap_date_prepared: '',
    time_unit_leader: '',
    contact_info: '',
    tactical_operations: [],
    incident_activities: [],
    operational_objectives: '',
    time_constraints: '',
    special_instructions: '',
    prepared_by: '',
    date_prepared: ''
  });
  const [loading, setLoading] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // Fetch entries
  const fetchEntries = async (silent = false) => {
    if (!currentExercise?.id) return;
    
    if (!silent) setLoading(true);
    try {
      const response = await fetch(`${API}/time-unit?exercise_id=${currentExercise.id}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
        setLastSynced(new Date());
      }
    } catch (error) {
      if (!silent) console.error('Error fetching Time Unit entries:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Fetch map markers as locations for dropdown - filtered by current exercise
  const fetchMapMarkers = async () => {
    try {
      const exerciseId = currentExercise?.id;
      const url = exerciseId 
        ? `${API}/map-objects?type=marker&exercise_id=${exerciseId}`
        : `${API}/map-objects?type=marker`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setLocations(data.map(marker => ({ id: marker.id, name: marker.name })));
      }
    } catch (error) {
      console.error('Error fetching map markers:', error);
    }
  };

  useEffect(() => {
    fetchEntries();
    fetchMapMarkers();
  }, [currentExercise?.id]);

  // Setup polling for real-time sync (every 30 seconds)
  useEffect(() => {
    if (currentExercise?.id) {
      const pollInterval = setInterval(() => {
        fetchEntries(true);
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [currentExercise?.id]);

  // Initialize form
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        exercise_name: editingEntry.exercise_name || currentExercise?.exercise_name || '',
        incident_name: editingEntry.incident_name || '',
        operational_period_number: editingEntry.operational_period_number || '',
        operational_period_start: editingEntry.operational_period_start || '',
        operational_period_end: editingEntry.operational_period_end || '',
        iap_prepared_by: editingEntry.iap_prepared_by || '',
        iap_date_prepared: editingEntry.iap_date_prepared || '',
        time_unit_leader: editingEntry.time_unit_leader || '',
        contact_info: editingEntry.contact_info || '',
        tactical_operations: editingEntry.tactical_operations || [],
        incident_activities: editingEntry.incident_activities || [],
        operational_objectives: editingEntry.operational_objectives || '',
        time_constraints: editingEntry.time_constraints || '',
        special_instructions: editingEntry.special_instructions || '',
        prepared_by: editingEntry.prepared_by || '',
        date_prepared: editingEntry.date_prepared || ''
      });
    } else {
      setFormData({
        exercise_name: currentExercise?.exercise_name || '',
        incident_name: '',
        operational_period_number: '',
        operational_period_start: '',
        operational_period_end: '',
        iap_prepared_by: '',
        iap_date_prepared: '',
        time_unit_leader: '',
        contact_info: '',
        tactical_operations: [],
        incident_activities: [],
        operational_objectives: '',
        time_constraints: '',
        special_instructions: '',
        prepared_by: '',
        date_prepared: ''
      });
    }
  }, [editingEntry, currentExercise]);

  // Tactical operations handlers
  const addTacticalOperation = () => {
    const newItem = {
      id: Date.now().toString(),
      operation_name: '',
      description: '',
      start_time: '',
      end_time: '',
      duration: '',
      assigned_to: '',
      status: 'Planned'
    };
    setFormData(prev => ({
      ...prev,
      tactical_operations: [...prev.tactical_operations, newItem]
    }));
  };

  const updateTacticalOperation = (index, field, value) => {
    const updated = [...formData.tactical_operations];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, tactical_operations: updated }));
  };

  const deleteTacticalOperation = (index) => {
    setFormData(prev => ({
      ...prev,
      tactical_operations: prev.tactical_operations.filter((_, i) => i !== index)
    }));
  };

  // Incident activities handlers
  const addIncidentActivity = () => {
    const newItem = {
      id: Date.now().toString(),
      activity_name: '',
      activity_type: '',
      start_time: '',
      end_time: '',
      location: '',
      personnel_involved: '',
      notes: ''
    };
    setFormData(prev => ({
      ...prev,
      incident_activities: [...prev.incident_activities, newItem]
    }));
  };

  const updateIncidentActivity = (index, field, value) => {
    const updated = [...formData.incident_activities];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, incident_activities: updated }));
  };

  const deleteIncidentActivity = (index) => {
    setFormData(prev => ({
      ...prev,
      incident_activities: prev.incident_activities.filter((_, i) => i !== index)
    }));
  };

  // Save entry
  const saveEntry = async () => {
    if (!currentExercise?.id) {
      alert('No exercise selected');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        exercise_id: currentExercise.id,
        exercise_name: currentExercise.exercise_name
      };

      let response;
      if (editingEntry) {
        response = await fetch(`${API}/time-unit/${editingEntry.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API}/time-unit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        alert(editingEntry ? 'Entry updated successfully!' : 'Entry created successfully!');
        fetchEntries();
        setView('list');
        setEditingEntry(null);
      } else {
        alert('Failed to save entry');
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Error saving entry');
    } finally {
      setLoading(false);
    }
  };

  // Delete entry
  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/time-unit/${entryId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Entry deleted successfully!');
        fetchEntries();
      } else {
        alert('Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry');
    } finally {
      setLoading(false);
    }
  };

  // Comprehensive Print Function (DOM-safe via writeSanitizedHTML)
  const handlePrint = () => {
    const data = formData || {};
    const exerciseName = data.exercise_name || currentExercise?.exercise_name || 'N/A';
    const incidentName = data.incident_name || 'N/A';
    const generated = new Date().toLocaleString();

    const tableSection = (title, columns, rows, emptyMsg) => {
      if (!rows || rows.length === 0) {
        return `<div class="section"><h2>${title}</h2><p class="empty">${emptyMsg}</p></div>`;
      }
      const head = columns.map(c => `<th>${c.label}</th>`).join('');
      const body = rows.map(r =>
        `<tr>${columns.map(c => `<td>${r[c.key] != null && r[c.key] !== '' ? r[c.key] : ''}</td>`).join('')}</tr>`
      ).join('');
      return `
        <div class="section">
          <h2>${title} <span class="count">(${rows.length})</span></h2>
          <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
        </div>`;
    };

    const infoItem = (label, value) =>
      `<div class="info-item"><div class="info-label">${label}</div><div class="info-value">${value || 'N/A'}</div></div>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Time Unit Report - ${exerciseName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.5; color: #111827; }
            h1 { color: #3b82f6; text-align: center; border-bottom: 3px solid #3b82f6; padding-bottom: 10px; margin-bottom: 6px; }
            .sub { text-align: center; color: #6b7280; margin-bottom: 24px; font-size: 12px; }
            h2 { color: #3b82f6; margin-top: 26px; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px; font-size: 16px; }
            .count { color: #6b7280; font-size: 13px; font-weight: normal; }
            .section { margin-bottom: 22px; page-break-inside: avoid; }
            .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 10px 0; }
            .info-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 10px 0; }
            .info-grid-1 { display: grid; grid-template-columns: 1fr; gap: 10px; margin: 10px 0; }
            .info-item { padding: 8px 10px; background: #f9fafb; border-left: 3px solid #3b82f6; }
            .info-label { font-weight: bold; color: #6b7280; font-size: 11px; text-transform: uppercase; }
            .info-value { color: #111827; margin-top: 3px; font-size: 13px; white-space: pre-wrap; }
            table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
            th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; }
            th { background: #eff6ff; font-weight: bold; color: #1e40af; }
            tr:nth-child(even) { background: #f9fafb; }
            .empty { color: #9ca3af; font-style: italic; font-size: 12px; }
            .footer { text-align: center; color: #6b7280; margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 11px; }
            @media print { body { padding: 12px; } .section { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Time Unit Report</h1>
          <div class="sub"><strong>Exercise:</strong> ${exerciseName} &nbsp;•&nbsp; <strong>Incident:</strong> ${incidentName} &nbsp;•&nbsp; <strong>Generated:</strong> ${generated}</div>

          <div class="section">
            <h2>Operational Period Information</h2>
            <div class="info-grid">
              ${infoItem('Exercise Name', exerciseName)}
              ${infoItem('Incident Name', incidentName)}
              ${infoItem('Operational Period #', data.operational_period_number)}
              ${infoItem('Period Start', data.operational_period_start)}
              ${infoItem('Period End', data.operational_period_end)}
              ${infoItem('Time Unit Leader', data.time_unit_leader)}
              ${infoItem('Contact Info', data.contact_info)}
            </div>
          </div>

          <div class="section">
            <h2>Incident Action Plan (IAP)</h2>
            <div class="info-grid">
              ${infoItem('IAP Prepared By', data.iap_prepared_by)}
              ${infoItem('IAP Date Prepared', data.iap_date_prepared)}
            </div>
            <div class="info-grid-1">
              ${infoItem('Operational Objectives', data.operational_objectives)}
            </div>
          </div>

          ${tableSection('Tactical Operations',
            [
              { key: 'operation_name', label: 'Operation' },
              { key: 'description', label: 'Description' },
              { key: 'start_time', label: 'Start' },
              { key: 'end_time', label: 'End' },
              { key: 'duration', label: 'Duration' },
              { key: 'assigned_to', label: 'Assigned To' },
              { key: 'status', label: 'Status' }
            ],
            data.tactical_operations,
            'No tactical operations.')}

          ${tableSection('Incident Activities',
            [
              { key: 'activity_name', label: 'Activity' },
              { key: 'activity_type', label: 'Type' },
              { key: 'start_time', label: 'Start' },
              { key: 'end_time', label: 'End' },
              { key: 'location', label: 'Location' },
              { key: 'personnel_involved', label: 'Personnel' },
              { key: 'notes', label: 'Notes' }
            ],
            data.incident_activities,
            'No incident activities.')}

          <div class="section">
            <h2>Additional Information</h2>
            <div class="info-grid-1">
              ${infoItem('Time Constraints', data.time_constraints)}
              ${infoItem('Special Instructions', data.special_instructions)}
            </div>
            <div class="info-grid">
              ${infoItem('Prepared By', data.prepared_by)}
              ${infoItem('Date Prepared', data.date_prepared)}
            </div>
          </div>

          <div class="footer">
            <div><strong>Time Unit Report</strong> &nbsp;|&nbsp; ${exerciseName}</div>
            <div>Powered by EXRSIM</div>
          </div>
        </body>
      </html>
    `;

    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  // List view
  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Time Unit Collection</h1>
            <p className={`${theme.colors.textMuted}`}>Track operational periods and incident activities within the IAP</p>
          </div>
          <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="h-4 w-4 mr-2" />
            New Entry
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className={`${theme.colors.textMuted}`}>Loading...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <Clock className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <p className={`${theme.colors.textMuted} mb-4`}>No time unit entries found</p>
            <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
              Create First Entry
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry) => (
              <Card key={entry.id} className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardHeader>
                  <CardTitle className={`${theme.colors.textPrimary}`}>
                    {entry.incident_name || 'Untitled Entry'} - Period {entry.operational_period_number || 'N/A'}
                  </CardTitle>
                  <CardDescription className={`${theme.colors.textMuted}`}>
                    {entry.operational_period_start} to {entry.operational_period_end}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingEntry(entry);
                        setView('form');
                      }}
                      className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteEntry(entry.id)}
                      className="border-red-500 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Form view
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex justify-between items-center print:hidden">
        <Button onClick={() => {
          setView('list');
          setEditingEntry(null);
        }} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <Button onClick={handlePrint} className="bg-blue-500 hover:bg-blue-600">
          <Printer className="h-4 w-4 mr-2" />
          Print Time Unit
        </Button>
      </div>

      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Time Unit Collection</h1>
        </div>

        {/* Basic Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Operational Period Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Exercise Name</Label>
                <div className={`p-2 bg-gray-800 rounded border border-gray-600`}>
                  <span className={`${theme.colors.textPrimary}`}>{currentExercise?.exercise_name || 'N/A'}</span>
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Incident Name</Label>
                <Input
                  value={formData.incident_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, incident_name: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Operational Period Number</Label>
                <Input
                  value={formData.operational_period_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, operational_period_number: e.target.value }))}
                  placeholder="e.g., 1, 2, 3"
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Period Start</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.operational_period_start ? formData.operational_period_start.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.operational_period_start ? formData.operational_period_start.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, operational_period_start: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.operational_period_start ? formData.operational_period_start.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.operational_period_start ? formData.operational_period_start.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, operational_period_start: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Period End</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.operational_period_end ? formData.operational_period_end.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.operational_period_end ? formData.operational_period_end.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, operational_period_end: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.operational_period_end ? formData.operational_period_end.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.operational_period_end ? formData.operational_period_end.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, operational_period_end: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Time Unit Leader</Label>
                <Input
                  value={formData.time_unit_leader}
                  onChange={(e) => setFormData(prev => ({ ...prev, time_unit_leader: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Contact Information</Label>
                <Input
                  value={formData.contact_info}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_info: e.target.value }))}
                  placeholder="Email or Phone"
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* IAP Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Incident Action Plan (IAP)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>IAP Prepared By</Label>
                <Input
                  value={formData.iap_prepared_by}
                  onChange={(e) => setFormData(prev => ({ ...prev, iap_prepared_by: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>IAP Date Prepared</Label>
                <Input
                  type="date"
                  value={formData.iap_date_prepared}
                  onChange={(e) => setFormData(prev => ({ ...prev, iap_date_prepared: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Operational Objectives</Label>
              <Textarea
                value={formData.operational_objectives}
                onChange={(e) => setFormData(prev => ({ ...prev, operational_objectives: e.target.value }))}
                rows={3}
                placeholder="Tactical goals for this operational period..."
                className={`bg-gray-800 border-gray-600 text-white`}
              />
            </div>
          </CardContent>
        </Card>

        {/* Tactical Operations Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Tactical Operations</CardTitle>
            <CardDescription className={`${theme.colors.textMuted}`}>
              Track timeframes for completing tactical operations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`${theme.colors.border} border-b`}>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Operation Name</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Description</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Start Time</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>End Time</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Duration</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Assigned To</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Status</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2 print:hidden`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.tactical_operations.map((item, index) => (
                    <tr key={item.id} className={`${theme.colors.border} border-b`}>
                      <td className="p-2">
                        <Input
                          value={item.operation_name}
                          onChange={(e) => updateTacticalOperation(index, 'operation_name', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[150px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Textarea
                          value={item.description}
                          onChange={(e) => updateTacticalOperation(index, 'description', e.target.value)}
                          rows={1}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[200px]`}
                        />
                      </td>
                      <td className="p-2">
                        <div className="grid grid-cols-2 gap-1">
                          <Input
                            type="date"
                            value={item.start_time ? item.start_time.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = item.start_time ? item.start_time.split('T')[1] : '00:00';
                              updateTacticalOperation(index, 'start_time', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={item.start_time ? item.start_time.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = item.start_time ? item.start_time.split('T')[0] : '';
                                updateTacticalOperation(index, 'start_time', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="grid grid-cols-2 gap-1">
                          <Input
                            type="date"
                            value={item.end_time ? item.end_time.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = item.end_time ? item.end_time.split('T')[1] : '00:00';
                              updateTacticalOperation(index, 'end_time', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={item.end_time ? item.end_time.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = item.end_time ? item.end_time.split('T')[0] : '';
                                updateTacticalOperation(index, 'end_time', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.duration}
                          onChange={(e) => updateTacticalOperation(index, 'duration', e.target.value)}
                          placeholder="e.g., 2 hours"
                          className={`bg-gray-800 border-gray-600 text-white w-28`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.assigned_to}
                          onChange={(e) => updateTacticalOperation(index, 'assigned_to', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[120px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Select value={item.status} onValueChange={(value) => updateTacticalOperation(index, 'status', value)}>
                          <SelectTrigger className={`bg-gray-800 border-gray-600 text-white w-32`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Planned">Planned</SelectItem>
                            <SelectItem value="In Progress">In Progress</SelectItem>
                            <SelectItem value="Completed">Completed</SelectItem>
                            <SelectItem value="Delayed">Delayed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2 print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteTacticalOperation(index)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addTacticalOperation} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Tactical Operation
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Incident Activities Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Incident Activities</CardTitle>
            <CardDescription className={`${theme.colors.textMuted}`}>
              Track all incident-related activities and their timeframes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`${theme.colors.border} border-b`}>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Activity Name</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Type</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Start Time</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>End Time</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Location</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Personnel</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Notes</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2 print:hidden`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.incident_activities.map((item, index) => (
                    <tr key={item.id} className={`${theme.colors.border} border-b`}>
                      <td className="p-2">
                        <Input
                          value={item.activity_name}
                          onChange={(e) => updateIncidentActivity(index, 'activity_name', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[150px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.activity_type}
                          onChange={(e) => updateIncidentActivity(index, 'activity_type', e.target.value)}
                          placeholder="e.g., Briefing, Operation"
                          className={`bg-gray-800 border-gray-600 text-white min-w-[120px]`}
                        />
                      </td>
                      <td className="p-2">
                        <div className="grid grid-cols-2 gap-1">
                          <Input
                            type="date"
                            value={item.start_time ? item.start_time.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = item.start_time ? item.start_time.split('T')[1] : '00:00';
                              updateIncidentActivity(index, 'start_time', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={item.start_time ? item.start_time.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = item.start_time ? item.start_time.split('T')[0] : '';
                                updateIncidentActivity(index, 'start_time', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="grid grid-cols-2 gap-1">
                          <Input
                            type="date"
                            value={item.end_time ? item.end_time.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = item.end_time ? item.end_time.split('T')[1] : '00:00';
                              updateIncidentActivity(index, 'end_time', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={item.end_time ? item.end_time.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = item.end_time ? item.end_time.split('T')[0] : '';
                                updateIncidentActivity(index, 'end_time', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </td>
                      <td className="p-2">
                        <select
                          value={item.location}
                          onChange={(e) => updateIncidentActivity(index, 'location', e.target.value)}
                          className={`w-full p-2 rounded bg-gray-800 border-gray-600 text-white min-w-[120px]`}
                        >
                          <option value="">Select Location</option>
                          {locations.map((loc) => (
                            <option key={loc.id} value={loc.name}>
                              {loc.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.personnel_involved}
                          onChange={(e) => updateIncidentActivity(index, 'personnel_involved', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[120px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Textarea
                          value={item.notes}
                          onChange={(e) => updateIncidentActivity(index, 'notes', e.target.value)}
                          rows={1}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[150px]`}
                        />
                      </td>
                      <td className="p-2 print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteIncidentActivity(index)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addIncidentActivity} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Incident Activity
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Additional Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Additional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Time Constraints</Label>
              <Textarea
                value={formData.time_constraints}
                onChange={(e) => setFormData(prev => ({ ...prev, time_constraints: e.target.value }))}
                rows={3}
                placeholder="Any time-related constraints or limitations..."
                className={`bg-gray-800 border-gray-600 text-white`}
              />
            </div>
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Special Instructions</Label>
              <Textarea
                value={formData.special_instructions}
                onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))}
                rows={3}
                placeholder="Special instructions for this operational period..."
                className={`bg-gray-800 border-gray-600 text-white`}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Prepared By</Label>
                <Input
                  value={formData.prepared_by}
                  onChange={(e) => setFormData(prev => ({ ...prev, prepared_by: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Date Prepared</Label>
                <Input
                  type="date"
                  value={formData.date_prepared}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_prepared: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end print:hidden">
          <Button onClick={saveEntry} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : (editingEntry ? 'Update' : 'Save')} Entry
          </Button>
        </div>
      </div>
    </div>
  );
};

// Procurement Form

export default TimeUnitForm;
